import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import { ThemeContext, type Theme, type ResolvedTheme, type Branding } from './ThemeContext'
import { brandShades, ensureContrastOnWhite } from '@/shared/lib/contrast'

const STORAGE_KEY = 'basiyo-theme'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseHex(hex: string): [number, number, number] | null {
  const clean = hex.replace('#', '')
  if (clean.length !== 6) return null
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null
  return [r, g, b]
}

function clamp(v: number): number {
  return Math.max(0, Math.min(255, v))
}

function rgbStr(r: number, g: number, b: number): string {
  return `${clamp(r)} ${clamp(g)} ${clamp(b)}`
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      return (localStorage.getItem(STORAGE_KEY) as Theme) ?? 'system'
    } catch {
      return 'system'
    }
  })

  // Dark mode is disabled product-wide. The app ships only a light theme
  // (there are no `dark:` styles), so following the OS dark preference (the
  // default `theme === 'system'`) half-darkened the UI by flipping the brand
  // tokens under `.dark`. Force light and never apply the `.dark` class,
  // regardless of system preference or any persisted choice.
  const resolvedTheme: ResolvedTheme = 'light'

  useEffect(() => {
    document.documentElement.classList.remove('dark')
  }, [])

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t)
    try {
      localStorage.setItem(STORAGE_KEY, t)
    } catch {
      // localStorage unavailable (private browsing, etc.) - still works in-memory
    }
  }, [])

  // Inject per-host branding by overriding CSS custom properties on :root
  const applyBranding = useCallback((branding: Branding) => {
    const el = document.documentElement

    if (branding.primaryColor) {
      const parsed = parseHex(branding.primaryColor)
      if (parsed) {
        // F-014: a light host colour would put white button text, links and
        // the active tab under 4.5:1. Darken it just enough to pass.
        const base = ensureContrastOnWhite(parsed)
        // N-7: the same shade rule the default tokens in index.css follow.
        const { light, dark } = brandShades(base)
        el.style.setProperty('--brand-orange', rgbStr(...base))
        el.style.setProperty('--brand-orange-light', rgbStr(...light))
        el.style.setProperty('--brand-orange-dark', rgbStr(...dark))
      }
    }

    if (branding.neutralColor) {
      const rgb = parseHex(branding.neutralColor)
      if (rgb) {
        const [r, g, b] = rgb
        el.style.setProperty('--brand-rail', rgbStr(r, g, b))
        el.style.setProperty('--brand-rail-light', rgbStr(r + 15, g + 15, b + 14))
      }
    }
  }, [])

  // Remove inline overrides - falls back to the CSS file definitions
  const resetBranding = useCallback(() => {
    const el = document.documentElement
    const vars = [
      '--brand-orange',
      '--brand-orange-light',
      '--brand-orange-dark',
      '--brand-rail',
      '--brand-rail-light',
    ]
    vars.forEach((v) => el.style.removeProperty(v))
  }, [])

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, applyBranding, resetBranding }),
    [theme, resolvedTheme, setTheme, applyBranding, resetBranding],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
